import moment from "moment";

/**
 * Lease statuses that mean the resident is actively housed (moved in / on an
 * active lease). Mirrors `isActiveLeaseStatus` in the API
 * (`resident-portal-api/src/helpers/index.ts`) — keep the two in sync.
 */
export const ACTIVE_LEASE_STATUSES = [
  "Current",
  "Pending Renewal",
  "Under Eviction",
  "Notice to Vacate",
  "Month to Month",
];

/**
 * True when a lease status represents an active/moved-in residency.
 */
export function isActiveLeaseStatus(status?: string | null): boolean {
  return !!status && ACTIVE_LEASE_STATUSES.includes(status);
}

/**
 * LaunchDarkly key `tp-home-lease-switcher`.
 * An explicit true or false wins. Until the flag exists, the switcher stays on
 * QA and localhost and stays off everywhere else.
 */
export function isHomeLeaseSwitcherEnabled(flag: unknown): boolean {
  if (typeof flag === "boolean") return flag;
  if (typeof window === "undefined") return false;
  const host = window.location.hostname;
  return (
    host === "localhost" ||
    host === "127.0.0.1" ||
    host.endsWith(".qa.tenantport.com")
  );
}

/**
 * Get the current lease from user data.
 */
export function getCurrentLease(userData: any): any | null {
  const currentLease = userData?.user?.currentLease;
  if (currentLease) return currentLease;
  if (userData?.user?.person?.leaseLinks?.length > 0) {
    return userData.user.person.leaseLinks[0].lease;
  }
  return null;
}

/**
 * Check if a document is a renewal offer within 90 days of the lease end date.
 */
export function isRenewalOfferDoc(
  doc: any,
  leaseEndDate: string | Date
): boolean {
  if (!/^Renewal Offer/i.test(doc.name)) return false;
  const diffInDays = moment(leaseEndDate).diff(
    moment(doc.dateAttached),
    "days"
  );
  return diffInDays >= 0 && diffInDays <= 90;
}

/**
 * Find the newest renewal offer document within 90 days of the lease end date.
 */
export function findRenewalOfferDoc(
  docs: any[],
  leaseEndDate: string | Date
): any | null {
  const matches = (docs || []).filter((doc) =>
    isRenewalOfferDoc(doc, leaseEndDate)
  );
  if (matches.length === 0) return null;
  return matches.reduce((newest, doc) => {
    const newestTs = new Date(newest.dateAttached || 0).getTime();
    const docTs = new Date(doc.dateAttached || 0).getTime();
    return docTs > newestTs ? doc : newest;
  });
}
